/**
 * Help Chat bot settings, stored as Settings on the user's own Help Chat record.
 * Option ids must match functions-common/agents/helpChat/settings.js, which
 * re-validates everything before it reaches a prompt.
 */

const DEFAULT_BOT_NAME = "Ray";
const MAX_NAME_LENGTH = 40;
const MAX_CUSTOM_PERSONALITY_LENGTH = 500;
const MAX_ADDITIONAL_CONTEXT_LENGTH = 1000;
const MAX_PROMPT_LENGTH = 400;
const MAX_UPLOAD_BYTES = 5 * 1024 * 1024;
const AVATAR_SIZE = 512;
const ACCEPTED_UPLOAD_TYPES = ["image/png", "image/jpeg", "image/webp", "image/gif"];
const AVATAR_URL_PREFIX = "https://firebasestorage.googleapis.com/";

const PERSONALITY_OPTIONS = [
  { id: "friendly", label: "Friendly & Helpful", description: "Warm, approachable, supportive" },
  { id: "professional", label: "Professional", description: "Clear, formal, business-appropriate" },
  { id: "concise", label: "Concise & Direct", description: "Short, to-the-point answers" },
  { id: "humorous", label: "Humorous / Witty", description: "Light humour while remaining helpful" },
  { id: "empathetic", label: "Empathetic", description: "Patient, understanding, emotionally aware" },
  { id: "enthusiastic", label: "Enthusiastic", description: "Upbeat, encouraging, energetic" },
  { id: "technical", label: "Technical", description: "Precise, detailed, expert tone" },
  { id: "casual", label: "Casual", description: "Relaxed, conversational" },
];

const PERSONALITY_MODE_OPTIONS = [
  { id: "append", label: "Add to the personality" },
  { id: "override", label: "Replace the personality" },
];

const RESPONSE_LENGTH_OPTIONS = [
  { id: "short", label: "Short" },
  { id: "medium", label: "Medium" },
  { id: "long", label: "Long" },
];

const EMOJI_OPTIONS = [
  { id: "none", label: "None" },
  { id: "light", label: "Light" },
  { id: "frequent", label: "Frequent" },
];

const LANGUAGE_OPTIONS = [
  { id: "auto", label: "Match my message" },
  { id: "en", label: "English" },
  { id: "es", label: "Spanish" },
  { id: "fr", label: "French" },
  { id: "de", label: "German" },
  { id: "it", label: "Italian" },
  { id: "pt", label: "Portuguese" },
  { id: "nl", label: "Dutch" },
  { id: "pl", label: "Polish" },
  { id: "ar", label: "Arabic" },
  { id: "hi", label: "Hindi" },
  { id: "zh", label: "Chinese" },
  { id: "ja", label: "Japanese" },
];

// Dark enough for white text on the user's message bubbles.
const ACCENT_OPTIONS = [
  { id: "", label: "Default" },
  { id: "#1d4ed8", label: "Blue" },
  { id: "#0f766e", label: "Teal" },
  { id: "#15803d", label: "Green" },
  { id: "#7e22ce", label: "Purple" },
  { id: "#be185d", label: "Pink" },
  { id: "#c2410c", label: "Orange" },
  { id: "#374151", label: "Graphite" },
];

const AVATAR_PROMPT_IDEAS = [
  "Friendly robot face, simple and clean, circular style",
  "Minimalist geometric icon of an AI assistant",
  "Cute cartoon animal mascot, smiling",
  "Professional headshot-style portrait of a helpful AI",
  "Abstract colorful logo, modern and simple",
];

const DEFAULT_SETTINGS = Object.freeze({
  BotName: DEFAULT_BOT_NAME,
  Personality: "friendly",
  CustomPersonality: "",
  CustomPersonalityMode: "append",
  ResponseLength: "medium",
  Language: "auto",
  Emoji: "none",
  AdditionalContext: "",
  AccentColor: "",
  Memory: true,
  Notifications: false,
});

function optionId(value, options, fallback) {
  return options.some((option) => option.id === value) ? value : fallback;
}

function text(value, maxLength) {
  return typeof value === "string" ? value.slice(0, maxLength) : "";
}

function botName(value) {
  const name = typeof value === "string" ? value.replace(/\s+/g, " ").trim().slice(0, MAX_NAME_LENGTH) : "";
  return name || DEFAULT_BOT_NAME;
}

function avatarUrl(avatar) {
  const url = avatar?.Url;
  return typeof url === "string" && url.startsWith(AVATAR_URL_PREFIX) ? url : null;
}

/** Stored settings merged over the defaults, with unknown values dropped. */
function resolveSettings(raw) {
  const source = raw && typeof raw === "object" ? raw : {};
  return {
    BotName: botName(source.BotName),
    Personality: optionId(source.Personality, PERSONALITY_OPTIONS, DEFAULT_SETTINGS.Personality),
    CustomPersonality: text(source.CustomPersonality, MAX_CUSTOM_PERSONALITY_LENGTH),
    CustomPersonalityMode: optionId(source.CustomPersonalityMode, PERSONALITY_MODE_OPTIONS, DEFAULT_SETTINGS.CustomPersonalityMode),
    ResponseLength: optionId(source.ResponseLength, RESPONSE_LENGTH_OPTIONS, DEFAULT_SETTINGS.ResponseLength),
    Language: optionId(source.Language, LANGUAGE_OPTIONS, DEFAULT_SETTINGS.Language),
    Emoji: optionId(source.Emoji, EMOJI_OPTIONS, DEFAULT_SETTINGS.Emoji),
    AdditionalContext: text(source.AdditionalContext, MAX_ADDITIONAL_CONTEXT_LENGTH),
    AccentColor: optionId(source.AccentColor, ACCENT_OPTIONS, DEFAULT_SETTINGS.AccentColor),
    Memory: source.Memory !== false,
    Notifications: source.Notifications === true,
    AvatarUrl: avatarUrl(source.Avatar),
  };
}

/** The fields the settings form writes. Avatar is written by the helpChatAvatar callable. */
function editableSettings(draft) {
  const { AvatarUrl, ...settings } = resolveSettings(draft);
  return {
    ...settings,
    CustomPersonality: settings.CustomPersonality.trim(),
    AdditionalContext: settings.AdditionalContext.trim(),
  };
}

function uploadProblem(file) {
  if (!file) return "Choose an image.";
  if (!ACCEPTED_UPLOAD_TYPES.includes(file.type)) return "Use a PNG, JPEG, WebP, or GIF image.";
  if (file.size > MAX_UPLOAD_BYTES) return "Use an image under 5 MB.";
  return null;
}

/** Centre-crop to a square, scaled to cover `size`. */
function squareCrop(width, height, size = AVATAR_SIZE) {
  const side = Math.min(width, height);
  return {
    sx: Math.round((width - side) / 2),
    sy: Math.round((height - side) / 2),
    side,
    size: Math.min(size, side) || size,
  };
}

export {
  ACCENT_OPTIONS,
  ACCEPTED_UPLOAD_TYPES,
  AVATAR_PROMPT_IDEAS,
  AVATAR_SIZE,
  DEFAULT_BOT_NAME,
  DEFAULT_SETTINGS,
  EMOJI_OPTIONS,
  LANGUAGE_OPTIONS,
  MAX_ADDITIONAL_CONTEXT_LENGTH,
  MAX_CUSTOM_PERSONALITY_LENGTH,
  MAX_NAME_LENGTH,
  MAX_PROMPT_LENGTH,
  MAX_UPLOAD_BYTES,
  PERSONALITY_MODE_OPTIONS,
  PERSONALITY_OPTIONS,
  RESPONSE_LENGTH_OPTIONS,
  avatarUrl,
  editableSettings,
  resolveSettings,
  squareCrop,
  uploadProblem,
};
